import { endOfMonth, endOfWeek, startOfMonth, startOfWeek, subDays, subWeeks } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { SelectedRangeOption } from '@anytag/shared-types';
import { cn } from '@anytag/shared-utils';
import { DayPickerRangeProps } from './day-picker-range';
import { getSelectedRange } from './helpers';
import { Period, DateRange } from './types';

interface CustomRangeProps {
  state: DateRange;
  onClickCallback: (range: DateRange) => void;
  initialPeriod?: Period;
  rangeList: DayPickerRangeProps['rangeList'];
  clearTitle: DayPickerRangeProps['clearTitle'];
  className?: string;
}
export const useCustomRange = ({
  state,
  onClickCallback,
  initialPeriod,
  rangeList,
  clearTitle,
  className
}: CustomRangeProps) => {
  const { t } = useTranslation();

  const highlightedRange = getSelectedRange(state.hoverFrom, state.hoverTo, initialPeriod);

  return rangeList?.length ? (
    <div className={cn('flex flex-col border-r-border bg-white text-size-input', className)}>
      {getAvailableRangeList({
        onClickCallback,
        initialPeriod,
        clearTitle
      }).map(({ option, text, onClick }) =>
        rangeList.includes(option) ? (
          <button
            key={option}
            onClick={onClick}
            className={cn(
              'cursor-pointer border-none p-2.5 text-left hover:bg-dark-gray',
              highlightedRange === option && 'bg-active text-white hover:bg-active'
            )}
          >
            {t(text)}
          </button>
        ) : null
      )}
    </div>
  ) : null;
};

const today = new Date();
const yesterday = subDays(today, 1);
const fistDayOdWeek = startOfWeek(today);
const sevenDaysAgo = subDays(today, 6);
const oneWeekAgo = subWeeks(today, 1);
const startLastWeek = startOfWeek(oneWeekAgo);
const endLastWeek = endOfWeek(oneWeekAgo);
const lastDayOfPrevMonth = subDays(startOfMonth(today), 1);

const getAvailableRangeList = ({
  onClickCallback,
  initialPeriod,
  clearTitle
}: Pick<CustomRangeProps, 'onClickCallback' | 'initialPeriod' | 'clearTitle'>) => [
  {
    option: SelectedRangeOption.TODAY,
    onClick: () => {
      onClickCallback({
        from: today,
        to: today,
        hoverTo: today,
        hoverFrom: today
      });
    },
    text: SelectedRangeOption.TODAY
  },
  {
    option: SelectedRangeOption.YESTERDAY,
    onClick: () => {
      onClickCallback({
        from: yesterday,
        to: yesterday,
        hoverTo: yesterday,
        hoverFrom: yesterday
      });
    },
    text: SelectedRangeOption.YESTERDAY
  },
  {
    option: SelectedRangeOption.THIS_WEEK,
    onClick: () => {
      onClickCallback({
        from: fistDayOdWeek,
        hoverFrom: fistDayOdWeek,
        to: today,
        hoverTo: today
      });
    },
    text: SelectedRangeOption.THIS_WEEK
  },
  {
    option: SelectedRangeOption.LAST_SEVEN_DAYS,
    onClick: () => {
      onClickCallback({
        from: sevenDaysAgo,
        hoverFrom: sevenDaysAgo,
        to: today,
        hoverTo: today
      });
    },
    text: SelectedRangeOption.LAST_SEVEN_DAYS
  },
  {
    option: SelectedRangeOption.LAST_WEEK,
    onClick: () => {
      onClickCallback({
        from: startLastWeek,
        hoverFrom: startLastWeek,
        to: endLastWeek,
        hoverTo: endLastWeek
      });
    },
    text: SelectedRangeOption.LAST_WEEK
  },
  {
    option: SelectedRangeOption.TODAY_TO_MONTH_AGO,
    onClick: () => {
      onClickCallback({
        from: subDays(today, 29),
        hoverTo: today,
        to: today,
        hoverFrom: subDays(today, 29)
      });
    },
    text: SelectedRangeOption.TODAY_TO_MONTH_AGO
  },
  {
    option: SelectedRangeOption.THIS_MONTH,
    onClick: () => {
      onClickCallback({
        from: startOfMonth(today),
        hoverTo: endOfMonth(today),
        to: endOfMonth(today),
        hoverFrom: startOfMonth(today)
      });
    },
    text: SelectedRangeOption.THIS_MONTH
  },
  {
    option: SelectedRangeOption.LAST_MONTH,
    onClick: () => {
      onClickCallback({
        from: startOfMonth(lastDayOfPrevMonth),
        hoverTo: lastDayOfPrevMonth,
        to: lastDayOfPrevMonth,
        hoverFrom: startOfMonth(lastDayOfPrevMonth)
      });
    },
    text: SelectedRangeOption.LAST_MONTH
  },
  {
    option: SelectedRangeOption.ALL_TIME,
    onClick: () => {
      const initialFrom = initialPeriod ? new Date(initialPeriod.startDate) : today;
      const initialTo = initialPeriod ? new Date(initialPeriod.endDate) : today;

      onClickCallback({
        from: initialFrom,
        hoverTo: initialTo,
        to: initialTo,
        hoverFrom: initialFrom
      });
    },
    text: SelectedRangeOption.ALL_TIME
  },
  { option: SelectedRangeOption.CUSTOM, text: SelectedRangeOption.CUSTOM }, // No onClick for "Custom" as per original
  {
    option: SelectedRangeOption.CLEAR,
    onClick: () => {
      onClickCallback({
        from: undefined,
        hoverTo: undefined,
        to: undefined,
        hoverFrom: undefined
      });
    },
    text: clearTitle || SelectedRangeOption.CLEAR
  }
];
